/**
 * canvas-engine/renderer/elements/DataBoundElement.jsx
 *
 * OWN per-type element renderer for `dataBound` elements (dynamic sections).
 *
 * ZERO REUSE: imports NOTHING from template-builder/ or academy-ui/src/template/.
 *
 * Inner-content vs positioning CONTRACT: renders ONLY the inner chrome + live
 * list, filling its positioned box. The parent CanvasRenderer owns positioning.
 * DOM output is identical in builder and reader modes — the ONLY thing that
 * varies is the injected `props.data`, which is the same value passed by both
 * the in-process Preview and the published Reader. (Req 4.3, 25.2)
 *
 * Live-data contract (Req 25.2): the authored chrome (title/subtitle) is part of
 * the editable element, but the LIST content is sourced at render time from
 * `props.data[element.binding.source]` (an array). It is never hand-authored.
 * When no data is provided (builder default / preview with no injected data) a
 * sensible placeholder list of stub items is rendered so the section still looks
 * populated. Pure / presentational — no fetching here.
 */

import React from 'react';
import { assetRef } from '../../model/assetRef';

function str(v) {
  return v == null ? '' : String(v);
}

function color(v, fallback) {
  return typeof v === 'string' && v.length > 0 ? v : fallback;
}

/**
 * Resolve the bound list array from the injected data using binding.source.
 * Supports either `data[source]` being an array directly or `{ items: [...] }`.
 * @returns {Array}
 */
function resolveBoundItems(data, source) {
  if (!data || typeof data !== 'object' || !source) return [];
  const raw = data[source];
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object' && Array.isArray(raw.items)) return raw.items;
  return [];
}

/**
 * Build a few placeholder/skeleton items so an unbound section still looks
 * populated in preview. Deterministic (no randomness) for parity. (Req 25.2)
 */
function placeholderItems(source) {
  const label = source ? String(source) : 'item';
  const out = [];
  for (let i = 1; i <= 4; i += 1) {
    out.push({
      id: `placeholder-${label}-${i}`,
      title: `${label.charAt(0).toUpperCase()}${label.slice(1)} ${i}`,
      description: 'Live content appears here at render time.',
      placeholder: true,
    });
  }
  return out;
}

/**
 * Render a single bound item card.
 */
function BoundItem({ item }) {
  const it = item && typeof item === 'object' ? item : {};
  const title = str(it.title) || str(it.name) || str(it.label);
  const description = str(it.description) || str(it.subtitle) || str(it.summary);
  const price = str(it.price);
  const asset = assetRef(it.image != null ? it.image : it.asset != null ? it.asset : it.thumbnail);
  const imageSrc = asset.displayLink || asset.storedLink || '';
  const isPlaceholder = it.placeholder === true;

  const cardStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    padding: 12,
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    background: '#ffffff',
    minWidth: 0,
    opacity: isPlaceholder ? 0.7 : 1,
  };

  return (
    <div className="ce-databound__item" style={cardStyle}>
      {imageSrc ? (
        <img
          src={imageSrc}
          alt={title}
          style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 8, display: 'block' }}
        />
      ) : (
        <div style={{ width: '100%', height: 120, background: '#f1f5f9', borderRadius: 8 }} aria-hidden="true" />
      )}
      {title ? <span style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>{title}</span> : null}
      {description ? <span style={{ fontSize: 13, lineHeight: 1.4, color: '#475569' }}>{description}</span> : null}
      {price ? <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{price}</span> : null}
    </div>
  );
}

/**
 * Render authored chrome plus a live (or placeholder) bound list.
 *
 * @param {object} props
 * @param {object} props.element normalized `dataBound` Canvas_Element
 * @param {object} [props.data] injected render-time data keyed by binding.source
 */
export default function DataBoundElement({ element, data }) {
  const content = (element && element.content) || {};
  const style = (element && element.style) || {};
  const binding = (element && element.binding) || {};
  const source = str(binding.source);

  const title = str(content.title);
  const subtitle = str(content.subtitle);

  const bound = resolveBoundItems(data, source);
  const items = bound.length > 0 ? bound : placeholderItems(source);

  const rootStyle = {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    overflow: 'hidden',
    background: color(style.background, 'transparent'),
    color: color(style.color, '#0f172a'),
  };

  const listStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: 14,
  };

  return (
    <div className="ce-databound" data-source={source || undefined} style={rootStyle}>
      {title || subtitle ? (
        <div className="ce-databound__chrome" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {title ? <span style={{ fontSize: 22, fontWeight: 700 }}>{title}</span> : null}
          {subtitle ? <span style={{ fontSize: 14, color: color(style.color, '#475569') }}>{subtitle}</span> : null}
        </div>
      ) : null}

      <div className="ce-databound__list" style={listStyle}>
        {items.map((item, index) => (
          <BoundItem key={str(item && item.id) || `databound-${index}`} item={item} />
        ))}
      </div>
    </div>
  );
}
